import React from 'react';
import { Tree, Button, Space, Dropdown, Menu } from 'antd';
import {
  PlusOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import type { TreeNode, TreeNodeType } from '../../shared/types';

interface ConfigTreeProps {
  treeData: TreeNode[];
  selectedKeys: React.Key[];
  expandedKeys: React.Key[];
  onSelect: (keys: React.Key[], info: any) => void;
  onExpand: (keys: React.Key[]) => void;
  onAddNode: (type: TreeNodeType, parent?: TreeNode) => void;
  loading?: boolean;
}

const ConfigTree: React.FC<ConfigTreeProps> = ({
  treeData,
  selectedKeys,
  expandedKeys,
  onSelect,
  onExpand,
  onAddNode,
  loading,
}) => {
  // 将 TreeNode 转换为 Ant Design Tree 的 DataNode
  const convertToDataNode = (node: TreeNode): DataNode => {
    return {
      key: node.key,
      title: (
        <span>
          {getIcon(node.type)} {node.title}
        </span>
      ),
      children: node.children?.map(convertToDataNode),
      isLeaf: node.isLeaf,
    };
  };

  // 获取节点图标
  const getIcon = (type: TreeNodeType) => {
    switch (type) {
      case 'service':
        return <AppstoreOutlined style={{ color: '#1890ff' }} />;
      case 'page':
        return <AppstoreOutlined style={{ color: '#52c41a' }} />;
      case 'component':
        return <AppstoreOutlined style={{ color: '#fa8c16' }} />;
      case 'dataset':
        return <DatabaseOutlined style={{ color: '#722ed1' }} />;
      case 'field':
        return <FileTextOutlined style={{ color: '#13c2c2' }} />;
      case 'thinking-mode':
        return <ThunderboltOutlined style={{ color: '#eb2f96' }} />;
      default:
        return null;
    }
  };

  // 处理添加按钮点击
  const handleAddClick = (parent: TreeNode) => {
    const menuItems: any[] = [];

    switch (parent.type) {
      case 'service':
        menuItems.push({ key: 'page', label: '添加页面' });
        break;
      case 'page':
        menuItems.push({ key: 'component', label: '添加组件' });
        break;
      case 'component':
        menuItems.push(
          { key: 'component', label: '添加子组件' },
          { key: 'dataset', label: '关联数据集' }
        );
        break;
      case 'dataset':
        menuItems.push({ key: 'field', label: '添加字段' });
        break;
      case 'thinking-mode':
        if (parent.key === 'thinking-modes') {
          menuItems.push({ key: 'thinking-mode', label: '添加思考方式' });
        }
        break;
    }

    if (menuItems.length === 0) {
      return;
    }

    if (menuItems.length === 1) {
      onAddNode(menuItems[0].key as TreeNodeType, parent);
    } else {
      // 显示下拉菜单
      const menu = (
        <Menu
          onClick={({ key }) => {
            onAddNode(key as TreeNodeType, parent);
          }}
        >
          {menuItems.map(item => (
            <Menu.Item key={item.key}>{item.label}</Menu.Item>
          ))}
        </Menu>
      );

      // 这里需要在实际使用时通过 Dropdown 实现
      // 暂时直接调用第一个选项
      onAddNode(menuItems[0].key as TreeNodeType, parent);
    }
  };

  const dataNodes = treeData.map(convertToDataNode);

  return (
    <Tree
      treeData={dataNodes}
      selectedKeys={selectedKeys}
      expandedKeys={expandedKeys}
      onSelect={onSelect}
      onExpand={onExpand}
      showLine
      showIcon={false}
      blockNode
    />
  );
};

export default ConfigTree;
